// src/components/themes/default/store/branch/BranchCartContext.tsx
"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export interface CartItem {
  id: number;
  name: string;
  price: number;
  originalPrice?: number;
  image?: string;
  quantity: number;
  category_name?: string;
}

interface BranchCartContextType {
  cart: CartItem[];
  addToCart: (product: any, openDrawer?: boolean) => void;
  buyNow: (product: any) => void;
  updateQuantity: (id: number, delta: number) => void;
  removeFromCart: (id: number) => void;
  clearCart: () => void;
  cartTotal: number;
  totalItemCount: number;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  isCheckoutOpen: boolean;
  setIsCheckoutOpen: (open: boolean) => void;
}

const BranchCartContext = createContext<BranchCartContextType | undefined>(undefined);

export function BranchCartProvider({
  children,
  subdomain,
  branchId,
}: {
  children: ReactNode;
  subdomain: string;
  branchId: number;
}) {
  const cartStorageKey = `xylo_branch_cart_${subdomain}_${branchId}`;
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem(cartStorageKey);
      if (saved) setCart(JSON.parse(saved));
    } catch {
      setCart([]);
    }
  }, [cartStorageKey]);

  const saveCart = (newCart: CartItem[]) => {
    setCart(newCart);
    if (typeof window !== "undefined") {
      localStorage.setItem(cartStorageKey, JSON.stringify(newCart));
    }
  };

  const addToCart = (p: any, openDrawer: boolean = true) => {
    const existingIndex = cart.findIndex((item) => item.id === p.id);
    let updated: CartItem[];

    if (existingIndex > -1) {
      updated = cart.map((item, idx) =>
        idx === existingIndex ? { ...item, quantity: item.quantity + 1 } : item
      );
    } else {
      updated = [
        ...cart,
        {
          id: p.id,
          name: p.name,
          price: Number(p.price) || 0,
          originalPrice: p.compare_price ? Number(p.compare_price) : undefined,
          image: p.primary_image || p.image,
          quantity: 1,
          category_name: p.category_name,
        },
      ];
    }

    saveCart(updated);
    if (openDrawer) setIsCartOpen(true);
  };

  const buyNow = (p: any) => {
    addToCart(p, false);
    setIsCheckoutOpen(true);
  };

  const updateQuantity = (id: number, delta: number) => {
    const updated = cart
      .map((item) => {
        if (item.id === id) {
          const newQty = item.quantity + delta;
          return newQty > 0 ? { ...item, quantity: newQty } : null;
        }
        return item;
      })
      .filter(Boolean) as CartItem[];

    saveCart(updated);
  };

  const removeFromCart = (id: number) => {
    const updated = cart.filter((item) => item.id !== id);
    saveCart(updated);
  };

  const clearCart = () => {
    saveCart([]);
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <BranchCartContext.Provider
      value={{
        cart,
        addToCart,
        buyNow,
        updateQuantity,
        removeFromCart,
        clearCart,
        cartTotal,
        totalItemCount,
        isCartOpen,
        setIsCartOpen,
        isCheckoutOpen,
        setIsCheckoutOpen,
      }}
    >
      {children}
    </BranchCartContext.Provider>
  );
}

export const useBranchCart = () => {
  const context = useContext(BranchCartContext);
  if (!context) {
    throw new Error("useBranchCart must be used within a BranchCartProvider");
  }
  return context;
};